import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Box,
  Typography,
  Slider,
  Divider,
  Grid,
} from "@mui/material";

const BreakdownDialog = ({ open, onClose, currentCa, setCurrentCa }) => {
  const [isStraightLine, setIsStraightLine] = useState(
    currentCa.straightLineMethod
  );
  const [breakdown, setBreakdown] = useState([...currentCa.AAbreakdown]);
  const [initialAllowance, setInitialAllowance] = useState(
    currentCa.IA || null
  );
  const [years, setYears] = useState(currentCa.numberOfYears || 1);

  useEffect(() => {
    if (currentCa) {
      setIsStraightLine(currentCa.straightLineMethod);
      setBreakdown([...currentCa.AAbreakdown].reverse()); // Reverse order for correct display
      setInitialAllowance(currentCa.IA || null);
      setYears(currentCa.numberOfYears || 1);
    }
  }, [currentCa, open]); // Depend on `currentCa` and `open`

  useEffect(() => {
    let availablePercentage = 100 - (initialAllowance || 0);

    if (isStraightLine) {
      const evenSplit = parseFloat((availablePercentage / years).toFixed(2));

      // Generate an array with (years - 1) elements filled with evenSplit
      let newBreakdown = Array(years).fill(evenSplit);

      // Adjust the last element to ensure the total sum is exactly 100%
      const totalAssigned = evenSplit * (years - 1);
      newBreakdown[0] = parseFloat(
        (availablePercentage - totalAssigned).toFixed(2)
      );

      setBreakdown(newBreakdown.reverse()); // Reverse for correct order
    } else {
      setBreakdown(Array(years).fill(0));
    }
  }, [isStraightLine, years, initialAllowance]);

  const handleBreakdownChange = (value, index) => {
    const updatedBreakdown = [...breakdown];
    updatedBreakdown[index] = parseFloat(value) || 0;
    setBreakdown(updatedBreakdown);
  };

  const handleSaveWithValidation = () => {
    // Ensure values are rounded correctly before summing
    const roundedBreakdown = breakdown.map(
      (val) => Math.round(val * 100) / 100
    );

    const totalAA = roundedBreakdown.reduce((sum, val) => sum + val, 0);
    const total = Math.round((totalAA + (initialAllowance || 0)) * 100) / 100; // Ensure 2-decimal precision

    if (
      !currentCa.isFieldVariable.IA &&
      !currentCa.isFieldVariable.numberOfYears &&
      total !== 100
    ) {
      alert("Total allocation (IA + AA) must be exactly 100%: " + total);
      return;
    }

    setCurrentCa({
      ...currentCa,
      straightLineMethod:
        currentCa.isFieldVariable.IA || currentCa.isFieldVariable.numberOfYears
          ? true
          : isStraightLine,
      AAbreakdown:
        currentCa.isFieldVariable.IA || currentCa.isFieldVariable.numberOfYears
          ? []
          : roundedBreakdown.reverse(), // Ensure it's rounded before saving
      IA: currentCa.allowanceType.IA ? initialAllowance : null,
      numberOfYears: currentCa.isFieldVariable.numberOfYears ? null : years,
    });

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0px 4px 12px rgba(96, 63, 151, 0.1)", // Match theme shadow
          backgroundColor: "background.paper", // #F5F5F5
        },
      }}
      aria-labelledby='breakdown-dialog-title'
    >
      <DialogTitle
        id='breakdown-dialog-title'
        sx={{
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          py: 2,
          px: 3,
        }}
      >
        <Typography variant='h6' fontWeight='bold' color='primary.main'>
          Set Allowance Breakdown
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 4, pt: 3 }}>
        <Box display='flex' flexDirection='column' gap={3} sx={{ mt: 3 }}>
          {/* Allowance Settings Section */}
          <Box
            p={2}
            borderRadius={2}
            border='1px solid'
            borderColor='divider'
            bgcolor='background.paper'
            sx={{
              transition:
                "border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
              "&:hover": {
                borderColor: "primary.main",
                boxShadow: "0px 2px 8px rgba(96, 63, 151, 0.1)",
              },
            }}
          >
            <Typography
              variant='subtitle1'
              fontWeight='bold'
              color='text.primary'
              gutterBottom
            >
              Allowance Settings
            </Typography>
            <Grid container spacing={2} alignItems='center'>
              {/* Number of Years */}
              <Grid item xs={12} sm={6}>
                <Box display='flex' alignItems='center' gap={1} mb={1}>
                  <Typography variant='subtitle1' color='text.primary'>
                    Number of Years:
                  </Typography>
                  <Box
                    sx={{
                      minWidth: 45,
                      textAlign: "center",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      backgroundColor: "primary.main", // #603F97
                      color: "primary.contrastText", // #ffffff
                      fontWeight: "bold",
                      fontSize: "14px",
                    }}
                  >
                    {years}
                  </Box>
                </Box>
                <Slider
                  value={years}
                  onChange={(e, val) => setYears(val)}
                  min={1}
                  max={30}
                  marks
                  valueLabelDisplay='auto'
                  disabled={currentCa.isFieldVariable.numberOfYears}
                  sx={{
                    color: "primary.main",
                    "& .MuiSlider-thumb": {
                      "&:hover, &.Mui-focusVisible": {
                        boxShadow: "0px 0px 0px 8px rgba(96, 63, 151, 0.16)",
                      },
                    },
                    "& .MuiSlider-rail": {
                      backgroundColor: "text.secondary",
                    },
                  }}
                  aria-label='Number of Years'
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentCa.isFieldVariable.numberOfYears}
                      onChange={() =>
                        setCurrentCa({
                          ...currentCa,
                          isFieldVariable: {
                            ...currentCa.isFieldVariable,
                            numberOfYears:
                              !currentCa.isFieldVariable.numberOfYears,
                          },
                          numberOfYears: !currentCa.isFieldVariable
                            .numberOfYears
                            ? years
                            : null,
                        })
                      }
                      color='primary'
                      sx={{
                        "& .MuiSwitch-switchBase": {
                          color: "text.secondary",
                          "&.Mui-checked": {
                            color: "primary.main",
                          },
                          "&.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "primary.main",
                          },
                        },
                      }}
                    />
                  }
                  label='Variable Years'
                  sx={{ color: "text.primary" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isStraightLine}
                      onChange={(e) => setIsStraightLine(e.target.checked)}
                      color='primary'
                      sx={{
                        "& .MuiSwitch-switchBase": {
                          color: "text.secondary",
                          "&.Mui-checked": {
                            color: "primary.main",
                          },
                          "&.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "primary.main",
                          },
                        },
                      }}
                    />
                  }
                  label='Straight Line Method'
                  sx={{ color: "text.primary" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentCa.isFieldVariable.IA}
                      onChange={() =>
                        setCurrentCa({
                          ...currentCa,
                          isFieldVariable: {
                            ...currentCa.isFieldVariable,
                            IA: !currentCa.isFieldVariable.IA,
                          },
                        })
                      }
                      color='primary'
                      disabled={!currentCa.allowanceType.IA}
                      sx={{
                        "& .MuiSwitch-switchBase": {
                          color: "text.secondary",
                          "&.Mui-checked": {
                            color: "primary.main",
                          },
                          "&.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "primary.main",
                          },
                          "&.Mui-disabled": {
                            color: "text.disabled",
                          },
                          "&.Mui-disabled + .MuiSwitch-track": {
                            backgroundColor: "text.disabled",
                          },
                        },
                      }}
                    />
                  }
                  label='Variable IA'
                  sx={{ color: "text.primary" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentCa.allowanceType.IA}
                      onChange={() => {
                        const newIAState = !currentCa.allowanceType.IA;
                        setCurrentCa({
                          ...currentCa,
                          allowanceType: {
                            ...currentCa.allowanceType,
                            IA: newIAState,
                          },
                          isFieldVariable: {
                            ...currentCa.isFieldVariable,
                            IA: !newIAState
                              ? false
                              : currentCa.isFieldVariable.IA,
                          },
                          IA: newIAState ? initialAllowance || 0 : null,
                        });
                        if (!newIAState) setInitialAllowance(null);
                      }}
                      color='primary'
                      sx={{
                        "& .MuiSwitch-switchBase": {
                          color: "text.secondary",
                          "&.Mui-checked": {
                            color: "primary.main",
                          },
                          "&.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "primary.main",
                          },
                        },
                      }}
                    />
                  }
                  label='Enable IA (Initial Allowance)'
                  sx={{ color: "text.primary" }}
                />
              </Grid>
              {currentCa.allowanceType.IA && !currentCa.isFieldVariable.IA && (
                <Grid item xs={12}>
                  <Typography variant='subtitle1' color='text.primary'>
                    Initial Allowance (%)
                  </Typography>
                  <TextField
                    label='Initial Allowance'
                    type='number'
                    value={initialAllowance || ""}
                    onChange={(e) =>
                      setInitialAllowance(parseFloat(e.target.value) || 0)
                    }
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "primary.main" },
                        "&:hover fieldset": {
                          borderColor: "#4F337A",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "text.secondary",
                      },
                    }}
                    inputProps={{ min: 0, max: 100 }}
                    required
                    aria-label='Initial Allowance Percentage'
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          <Divider sx={{ borderColor: "divider" }} />

          {/* AA Breakdown Section */}
          {!currentCa.isFieldVariable.IA &&
            !currentCa.isFieldVariable.numberOfYears && (
              <Box>
                <Typography
                  variant='subtitle1'
                  fontWeight='bold'
                  color='text.primary'
                  gutterBottom
                >
                  Annual Allowance Breakdown
                </Typography>
                {breakdown.map((value, index) => (
                  <Box
                    key={index}
                    display='flex'
                    alignItems='center'
                    gap={2}
                    mb={2}
                  >
                    <Typography
                      variant='body1'
                      color='text.primary'
                      sx={{ minWidth: 80 }}
                    >
                      Year {index + 1}:
                    </Typography>
                    <TextField
                      type='number'
                      value={value}
                      onChange={(e) =>
                        handleBreakdownChange(e.target.value, index)
                      }
                      disabled={isStraightLine}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "primary.main" },
                          "&:hover fieldset": {
                            borderColor: "#4F337A",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "text.secondary",
                        },
                      }}
                      inputProps={{ min: 0, max: 100 }}
                      aria-label={`Year ${
                        index + 1
                      } Annual Allowance Percentage`}
                    />
                    <Typography variant='body1' color='text.primary'>
                      %
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

          {/* Warning if IA or Number of Years is Variable */}
          {(currentCa.isFieldVariable.IA ||
            currentCa.isFieldVariable.numberOfYears) && (
            <Typography
              variant='body2'
              color='error.main'
              sx={{
                p: 2,
                borderRadius: 1,
                backgroundColor: "rgba(211, 29, 131, 0.04)", // Light tint of secondary
              }}
            >
              Note: Pre-setting Breakdown is disabled as IA or Number of Years
              is variable.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          onClick={onClose}
          color='secondary'
          sx={{
            textTransform: "none",
            color: "secondary.main", // #D31D83
            "&:hover": {
              backgroundColor: "rgba(211, 29, 131, 0.04)", // Light tint of secondary
            },
            transition: "background-color 0.2s ease-in-out",
          }}
          aria-label='Cancel breakdown'
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveWithValidation}
          color='primary'
          variant='contained'
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            px: 3,
            backgroundColor: "primary.main", // #603F97
            "&:hover": {
              backgroundColor: "#4F337A",
              boxShadow: "0px 4px 12px rgba(96, 63, 151, 0.2)",
            },
            transition:
              "background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
          }}
          aria-label='Save breakdown'
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BreakdownDialog;
